"use client";
import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import ThemeToggle from "./ThemeToggle";

const navLinks = [
  { name: "How it works", href: "/product" },
  { name: "Results", href: "/results" },
  // Temporarily hidden for now — re-enable when ready.
  // { name: "Pilot", href: "/pilot" },
  { name: "Pricing", href: "/pricing" },
  { name: "Enterprise", href: "/enterprise" },
  { name: "Resources", href: "/blogs" },
  { name: "Company", href: "/company" },
  { name: "Contact", href: "/contact" },
];

export default function NewHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePathname();

  // TODO (⚠️ DECISION): Confirm the production auth URLs. These fall back to
  // https://app.topgeo.ai/sign-in and /sign-up when the env vars are unset.
  const loginUrl = process.env.NEXT_PUBLIC_LOGIN_URL || "https://app.topgeo.ai/sign-in";

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-surface-raised border-b border-hairline">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-14">
          <div className="flex items-center justify-between h-[70px]">

            {/* Logo */}
            <Link href="/" className="flex-shrink-0">
              <img src="/logo.png" alt="TopGEO AI" className="h-8 w-auto dark:hidden" />
              <img src="/footer-logo.svg" alt="TopGEO AI" className="h-8 w-auto hidden dark:block" />
            </Link>

            {/* Nav — centered */}
            <nav className="hidden xl:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
              {navLinks.map(({ name, href }) => {
                const active = pathname === href || pathname.startsWith(href + "/");
                return (
                  <Link
                    key={name}
                    href={href}
                    className={`text-[15px] font-semibold transition-colors duration-150 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ${
                      active
                        ? "text-ink underline underline-offset-4 decoration-accent decoration-[1.5px]"
                        : "text-ink-muted hover:text-ink"
                    }`}
                  >
                    {name}
                  </Link>
                );
              })}
            </nav>

            {/* CTA — right */}
            <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
              <ThemeToggle />
              <a
                href={loginUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-4 py-2 font-semibold text-[14px] text-ink-muted border border-hairline rounded-lg hover:border-ink-muted hover:text-ink active:opacity-70 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
              >
                Log In
              </a>
              <Link
                href="/audit"
                className="inline-flex items-center justify-center px-5 py-2 bg-accent text-on-accent font-semibold text-[14px] rounded-lg hover:opacity-90 active:opacity-80 transition-opacity cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
              >
                Get Free Audit Report
              </Link>
            </div>

            <div className="xl:hidden flex items-center">
            <ThemeToggle />
            {/* Mobile hamburger — min 44×44px tap target */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
              className="flex flex-col items-center justify-center min-w-[44px] min-h-[44px] gap-[5px] rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <span className={`block w-[22px] h-[2px] bg-ink transition-transform duration-200 origin-center ${isMenuOpen ? "rotate-45 translate-y-[7px]" : ""}`} />
              <span className={`block w-[22px] h-[2px] bg-ink transition-opacity duration-200 ${isMenuOpen ? "opacity-0" : ""}`} />
              <span className={`block w-[22px] h-[2px] bg-ink transition-transform duration-200 origin-center ${isMenuOpen ? "-rotate-45 -translate-y-[7px]" : ""}`} />
            </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="xl:hidden border-t border-hairline bg-surface-raised px-6 py-5 flex flex-col gap-4">
            {navLinks.map(({ name, href }) => (
              <Link
                key={name}
                href={href}
                onClick={() => setIsMenuOpen(false)}
                className="text-[15px] font-semibold text-ink-muted hover:text-ink py-3"
              >
                {name}
              </Link>
            ))}
            <div className="border-t border-hairline pt-4 flex flex-col gap-3">
              <a
                href={loginUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMenuOpen(false)}
                className="w-full inline-flex items-center justify-center py-2.5 rounded-lg font-semibold text-sm border border-hairline text-ink-muted cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
              >
                Log In
              </a>
              <Link
                href="/audit"
                onClick={() => setIsMenuOpen(false)}
                className="w-full inline-flex items-center justify-center py-2.5 rounded-lg font-semibold text-sm bg-accent text-on-accent cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
              >
                Get Free Audit Report
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Spacer */}
      <div className="h-[70px]" />
    </>
  );
}
